/**
 * Casca do aplicativo, no padrao do Auth Manager (`Auth-API/srv-frontend-react-v1`).
 *
 * O Auth Manager e o app de referencia visual do ecossistema, e o portal fala a mesma lingua:
 * sidebar fixa com a marca e um botao de recolher, grupos de navegacao colapsaveis com titulo em
 * `--color-tech`, ICONE em cada item, item atual com barra de 3px; no rodape, avatar, tema e sair.
 * Barra superior translucida com o ESCOPO ativo a esquerda - aqui, a empresa (o tenant) - e a
 * saude da API a direita.
 *
 * A casca e do CATALOGO. A leitura de um relatorio e tela cheia, sem ela (spec 0003, R4) - ver
 * `report/ReportView.tsx`.
 *
 * COPIA da mesma peca do Auth, do NIG e do Report Manager. Continua DIVIDA REGISTRADA: quem for
 * promover ao `@ledger-glass/react`, compare as copias primeiro - o que tiver divergido dira o que
 * cada app precisou (aqui: a contagem nos itens).
 */

import { Icon, IconButton, type IconName } from '@ledger-glass/react'
import { useQuery } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

import { apiGet, setToken } from '../api/client'
import { fetchMe } from '../auth/api'
import { aplicarTema, gravarTema, lerTema, type Tema } from './theme'

/** O sidebar recolhido muda o desenho de cada item: so icone, com o rotulo no `title`. */
const Recolhido = createContext(false)

export interface AppShellProps {
  /** Navegacao: `NavGroup` e `NavLink`. */
  nav: ReactNode
  /** Escopo ativo, a esquerda da barra superior. No portal, a empresa. */
  scope?: ReactNode
  children: ReactNode
}

export function AppShell({ nav, scope, children }: AppShellProps) {
  const [recolhido, setRecolhido] = useState(false)
  const [tema, setTema] = useState<Tema>(lerTema)

  useEffect(() => {
    aplicarTema(tema)
    gravarTema(tema)
  }, [tema])

  return (
    <Recolhido.Provider value={recolhido}>
      <div className={`app-shell${recolhido ? ' app-shell--recolhido' : ''}`}>
        <a className="skip-link" href="#conteudo">
          Pular para o conteúdo
        </a>

        <aside aria-label="Navegação" className={`shell-sidebar${recolhido ? ' shell-sidebar--recolhido' : ''}`}>
          <div className="shell-brand">
            <div className="shell-brand__identity">
              <Icon className="shell-brand__icon" name="report" size={28} />
              <div className="shell-brand__copy">
                <strong>Report Viewer</strong>
                <span>Portal de relatórios</span>
              </div>
            </div>
            <div className="shell-brand__actions">
              <IconButton
                className={`shell-sidebar__toggle${recolhido ? ' shell-sidebar__toggle--recolhido' : ''}`}
                icon="chevronLeft"
                label={recolhido ? 'Expandir menu' : 'Recolher menu'}
                onClick={() => setRecolhido((atual) => !atual)}
              />
            </div>
          </div>

          <nav aria-label="Navegação principal">{nav}</nav>

          <Identidade tema={tema} onTema={setTema} />
        </aside>

        <div className="shell-workspace">
          <header className="shell-topbar">
            <div className="scope-control">{scope}</div>
            <div className="shell-utilities">
              <SaudeDaApi />
            </div>
          </header>
          <main className="shell-page" id="conteudo" tabIndex={-1}>
            {children}
          </main>
        </div>
      </div>
    </Recolhido.Provider>
  )
}

/**
 * Grupo de navegacao colapsavel. O titulo e o proprio botao de abrir/fechar, como no Auth Manager:
 * nao ha controle separado para achar.
 */
export function NavGroup({
  titulo,
  children,
  inicialmenteAberto = true,
}: {
  titulo: string
  children: ReactNode
  inicialmenteAberto?: boolean
}) {
  const recolhido = useContext(Recolhido)
  const [aberto, setAberto] = useState(inicialmenteAberto)
  const regiao = `grupo-${titulo.toLowerCase().replace(/\W+/g, '-')}`

  // Recolhido, o sidebar so mostra icones: o grupo nao tem onde pendurar o titulo.
  if (recolhido) return <>{children}</>

  return (
    <section className="shell-nav-group">
      <h2>
        <button
          aria-controls={regiao}
          aria-expanded={aberto}
          className="shell-nav-group__toggle"
          onClick={() => setAberto((atual) => !atual)}
          type="button"
        >
          <span>{titulo}</span>
          <Icon
            aria-hidden="true"
            className={aberto ? undefined : 'shell-nav-group__chevron--fechado'}
            name="chevronDown"
            size={16}
          />
        </button>
      </h2>
      <div hidden={!aberto} id={regiao}>
        {children}
      </div>
    </section>
  )
}

/**
 * Item de navegacao: icone + rotulo, com a contagem opcional a direita. O `title` leva o rotulo
 * inteiro: nome de workspace comprido vira reticencias no sidebar estreito.
 */
export function NavLink({
  icon,
  label,
  atual = false,
  contagem,
  onClick,
}: {
  icon: IconName
  label: string
  atual?: boolean
  contagem?: number
  onClick: () => void
}) {
  const recolhido = useContext(Recolhido)
  return (
    <button
      aria-current={atual ? 'page' : undefined}
      className={`shell-nav-link${recolhido ? ' shell-nav-link--compacto' : ''}`}
      onClick={onClick}
      title={label}
      type="button"
    >
      <Icon name={icon} />
      <span>{label}</span>
      {contagem === undefined ? null : <span className="shell-nav-link__contagem">{contagem}</span>}
    </button>
  )
}

/**
 * Cabecalho de pagina do ecossistema: sobretitulo em `--color-tech`, titulo grande, descricao e as
 * acoes a direita.
 */
export function PageHeader({
  sobretitulo,
  titulo,
  descricao,
  acoes,
}: {
  sobretitulo: string
  titulo: ReactNode
  descricao?: ReactNode
  acoes?: ReactNode
}) {
  return (
    <header className="page-header">
      <div className="page-header__texto">
        <p className="eyebrow">{sobretitulo}</p>
        <h1>{titulo}</h1>
        {descricao === undefined ? null : <p className="page-header__descricao">{descricao}</p>}
      </div>
      {acoes === undefined ? null : <div className="page-header__actions">{acoes}</div>}
    </header>
  )
}

// ---------------------------------------------------------------------- pecas internas

type Saude = 'disponivel' | 'verificando' | 'indisponivel'

const ROTULO_DA_SAUDE: Record<Saude, string> = {
  disponivel: 'API do Report Viewer disponível',
  verificando: 'Verificando a API…',
  indisponivel: 'API do Report Viewer indisponível',
}

/**
 * Ponto de saude, como o do Auth Manager: quando a API cai, a tela diz, em vez de cada acao falhar
 * com um erro diferente. Nao substitui monitoramento, mas e o minimo para quem esta olhando.
 */
function SaudeDaApi() {
  const consulta = useQuery({
    queryKey: ['saude-da-api'],
    queryFn: () => apiGet<{ status: string }>('/health'),
    refetchInterval: 60_000,
    retry: false,
  })
  const saude: Saude = consulta.isPending ? 'verificando' : consulta.isError ? 'indisponivel' : 'disponivel'
  const rotulo = ROTULO_DA_SAUDE[saude]

  return (
    <span aria-label={rotulo} className="api-health-indicator" data-health={saude} role="status" tabIndex={0}>
      <span aria-hidden="true" className="api-health-indicator__dot" />
      <span className="api-health-indicator__tooltip" role="tooltip">
        {rotulo}
      </span>
    </span>
  )
}

/** Inicial para o avatar: do nome, ou do e-mail. */
function inicial(nome: string | null | undefined, email: string | null | undefined): string {
  const fonte = nome?.trim() || email?.trim() || ''
  return Array.from(fonte)[0]?.toLocaleUpperCase() ?? '?'
}

/**
 * Rodape do sidebar: avatar, tema e sair - na ordem do Auth Manager. O avatar abre empresa, nome e
 * e-mail, como la.
 *
 * **O botao de sair NAO depende da identidade ter carregado.** Antes a conta sumia inteira quando o
 * `/auth/me` falhava - e com ela a unica forma de sair de uma sessao quebrada.
 */
function Identidade({ tema, onTema }: { tema: Tema; onTema: (tema: Tema) => void }) {
  const recolhido = useContext(Recolhido)
  const [detalhes, setDetalhes] = useState(false)
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, retry: false })

  const nome = me.data?.name ?? me.data?.email ?? null
  const proximoTema: Tema = tema === 'escuro' ? 'claro' : 'escuro'

  return (
    <footer className="shell-identity">
      <button
        aria-controls="identidade-detalhes"
        aria-expanded={detalhes}
        aria-label={detalhes ? 'Ocultar identidade' : 'Mostrar identidade'}
        className="shell-identity__avatar"
        onClick={() => setDetalhes((atual) => !atual)}
        type="button"
      >
        <span aria-hidden="true">{inicial(me.data?.name, me.data?.email)}</span>
      </button>

      <div className="shell-identity__copy" hidden={!detalhes} id="identidade-detalhes">
        {me.data?.tenant_name == null ? null : <span title={me.data.tenant_name}>{me.data.tenant_name}</span>}
        <strong title={nome ?? undefined}>{nome ?? 'Identidade não carregada'}</strong>
        {me.data?.email == null || me.data.email === nome ? null : <span title={me.data.email}>{me.data.email}</span>}
      </div>

      {recolhido ? null : (
        <div className="shell-identity__actions">
          <IconButton
            aria-pressed={tema === 'claro'}
            icon={tema === 'escuro' ? 'sun' : 'moon'}
            label={tema === 'escuro' ? 'Usar tema claro' : 'Usar tema escuro'}
            onClick={() => onTema(proximoTema)}
          />
          <IconButton
            icon="logOut"
            label="Sair"
            onClick={() => {
              setToken(null)
              window.location.reload()
            }}
          />
        </div>
      )}
    </footer>
  )
}
